import { Text, View } from "react-native";

import { Input } from "@/components/ui/Input";
import { type PriceRange, priceRangeLabel } from "@/lib/filters/priceRange";

/**
 * The two halves of `PriceRangeFilter` that are the same on every platform, so its native and web
 * variants differ only in the slider between them: the heading with the range as it reads, and the
 * Min / Max fields for an exact amount.
 */
export function PriceRangeHeading({ label, range }: { label: string; range: PriceRange; }) {
  return (
    <View className="flex-row items-center justify-between">
      <Text
        className="font-label text-xs uppercase tracking-wide text-subtle"
        accessibilityRole="header"
        // react-native-web renders an unleveled `header` as `<h1>`.
        aria-level={3}
      >
        {label}
      </Text>
      <Text className="font-num-bold text-sm text-foreground">{priceRangeLabel(range)}</Text>
    </View>
  );
}

/** The Min / Max fields, editing the same dollar-text range the slider does. */
export function PriceRangeFields(
  { value, onChange, testIDPrefix }: {
    value: PriceRange;
    onChange: (next: PriceRange) => void;
    testIDPrefix: string;
  },
) {
  return (
    <View className="flex-row items-center gap-2">
      <Input
        testID={`${testIDPrefix}-min-price`}
        accessibilityLabel="Minimum price in dollars"
        placeholder="Min $"
        value={value.min}
        onChangeText={(min) => onChange({ ...value, min })}
        keyboardType="decimal-pad"
        // `min-w-0`: a web `<input>` will not shrink below its intrinsic width.
        className="min-w-0 flex-1"
      />
      <Text className="font-body text-sm text-muted-foreground">to</Text>
      <Input
        testID={`${testIDPrefix}-max-price`}
        accessibilityLabel="Maximum price in dollars"
        placeholder="Max $"
        value={value.max}
        onChangeText={(max) => onChange({ ...value, max })}
        keyboardType="decimal-pad"
        className="min-w-0 flex-1"
      />
    </View>
  );
}
